// src/contexts/AuthContext.js

import React, { createContext, useState, useEffect } from 'react';
import apis from '../services/api';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false); // true if logged in, false if guest
  const [userRole, setUserRole] = useState(null);
  const [username, setUsername] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);

  const handleLogin = async () => {
    await checkAuth(); // Re-check authentication after login
  };

  const handleLogout = async () => {
    try {
      await apis.Security.logout();
      // Re-check authentication, which will set to Guest if applicable
      await checkAuth();
    } catch (err) {
      setIsAuthenticated(false);
      setUserRole(null);
      setUsername(null);
    }
  };

  const handleRegister = async (username, email, password) => {
    try {
      const response = await apis.Security.register({ username, email, password });
      if (response.role === 'User') {
        setIsAuthenticated(true);
        setUserRole('User');
        setUsername(username);
        return { success: true, message: response.message };
      } else {
        return { success: false, message: 'Registration failed.' };
      }
    } catch (err) {
      throw err;
    }
  };

  const checkAuth = async () => {
    try {
      const response = await apis.Security.me();
      if (response.isAuthenticated) {
        if (response.role !== 'Guest') {
          setIsAuthenticated(true);
          setUserRole(response.role);
          setUsername(response.username);
        } else {
          // role is 'Guest', set isAuthenticated accordingly
          setIsAuthenticated(false);
          setUserRole('Guest');
          setUsername(null);
        }
      } else {
        // Not authenticated, try guest access
        const guestResponse = await apis.Security.guestAccess();
        if (guestResponse.role === 'Guest') {
          setIsAuthenticated(false);
          setUserRole('Guest');
          setUsername(null);
        } else {
          setIsAuthenticated(false);
          setUserRole(null);
          setUsername(null);
        }
      }
    } catch (err) {
      try {
        const guestResponse = await apis.Security.guestAccess();
        if (guestResponse.role === 'Guest') {
          setIsAuthenticated(false);
          setUserRole('Guest');
          setUsername(null);
        } else {
          setIsAuthenticated(false);
          setUserRole(null);
          setUsername(null);
        }
      } catch (guestErr) {
        setIsAuthenticated(false);
        setUserRole(null);
        setUsername(null);
      }
    } finally {
      setLoadingAuth(false);
    }
  };

  useEffect(() => {
    checkAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loadingAuth) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-primary to-secondary">
        <div className="text-white text-xl">Loading...</div>
      </div>
    ); // Or a spinner
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, userRole, username, handleLogin, handleLogout, handleRegister }}>
      {children}
    </AuthContext.Provider>
  );
};
